import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Plus, Car, X, Trash } from '@phosphor-icons/react';
import toast, { Toaster } from 'react-hot-toast';

export default function Vehicles() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    patente: '',
    marca: '',
    modelo: '',
    año: new Date().getFullYear(),
    tipo: '',
    empresa_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vRes, cRes] = await Promise.all([
        api.get('/vehicles'),
        api.get('/companies')
      ]);
      setVehicles(vRes.data);
      setCompanies(cRes.data);
      
      if (user?.rol !== 'super_admin' && user?.empresa_id) {
        setForm(prev => ({ ...prev, empresa_id: user.empresa_id }));
      }
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, año: parseInt(form.año) };
      if (user?.rol !== 'super_admin') {
        payload.empresa_id = user.empresa_id;
      }
      await api.post('/vehicles', payload);
      toast.success('Vehículo creado');
      setShowModal(false);
      setForm({ patente: '', marca: '', modelo: '', año: new Date().getFullYear(), tipo: '', empresa_id: user?.empresa_id || '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando vehículo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este vehículo?')) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success('Vehículo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error eliminando');
    }
  };

  const getCompanyName = (id) => companies.find(c => c.id === id)?.nombre || '—';

  return (
    <div data-testid="vehicles-page">
      <Toaster position="top-right" />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">
            Flota · Móviles
          </p>
          <h1 className="text-4xl font-bold text-neutral-900 tracking-tight">
            Vehículos
          </h1>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="new-vehicle-btn"
          className="flex items-center px-4 py-2.5 bg-blue-700 text-white rounded-md hover:bg-blue-800 text-sm font-medium"
        >
          <Plus size={18} className="mr-2" weight="bold" />
          Nuevo Vehículo
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-neutral-500">Cargando...</div>
        ) : vehicles.length === 0 ? (
          <div className="p-12 text-center">
            <Car size={48} className="mx-auto text-neutral-300 mb-3" />
            <p className="text-neutral-500">No hay vehículos registrados</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="vehicles-table">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Patente</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Marca</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Modelo</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Año</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Tipo</th>
                {user?.rol === 'super_admin' && (
                  <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Empresa</th>
                )}
                <th className="text-right py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Acción</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`vehicle-row-${v.id}`}>
                  <td className="py-4 px-6 font-mono font-medium text-neutral-900">{v.patente}</td>
                  <td className="py-4 px-6 text-neutral-700">{v.marca}</td>
                  <td className="py-4 px-6 text-neutral-700">{v.modelo}</td>
                  <td className="py-4 px-6 text-neutral-700">{v.año}</td>
                  <td className="py-4 px-6 text-neutral-700">{v.tipo}</td>
                  {user?.rol === 'super_admin' && (
                    <td className="py-4 px-6 text-neutral-700">{getCompanyName(v.empresa_id)}</td>
                  )}
                  <td className="py-4 px-6 text-right">
                    <button
                      onClick={() => handleDelete(v.id)}
                      data-testid={`delete-vehicle-${v.id}`}
                      className="text-red-600 hover:bg-red-50 p-2 rounded"
                    >
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="vehicle-modal">
          <div className="bg-white rounded-md max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <h2 className="text-xl font-bold text-neutral-900 tracking-tight">Nuevo Vehículo</h2>
              <button onClick={() => setShowModal(false)} data-testid="close-vehicle-modal-btn" className="p-1 hover:bg-neutral-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="vehicle-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Patente *</label>
                  <input
                    type="text"
                    value={form.patente}
                    onChange={(e) => setForm({ ...form, patente: e.target.value.toUpperCase() })}
                    data-testid="vehicle-patente-input"
                    required
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Año *</label>
                  <input
                    type="number"
                    value={form.año}
                    onChange={(e) => setForm({ ...form, año: e.target.value })}
                    data-testid="vehicle-año-input"
                    required
                    min="1990"
                    max="2030"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Marca *</label>
                  <input
                    type="text"
                    value={form.marca}
                    onChange={(e) => setForm({ ...form, marca: e.target.value })}
                    data-testid="vehicle-marca-input"
                    required
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Modelo *</label>
                  <input
                    type="text"
                    value={form.modelo}
                    onChange={(e) => setForm({ ...form, modelo: e.target.value })}
                    data-testid="vehicle-modelo-input"
                    required
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Tipo *</label>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  data-testid="vehicle-tipo-select"
                  required
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm bg-white"
                >
                  <option value="">Selecciona tipo</option>
                  <option value="Camión">Camión</option>
                  <option value="Camioneta">Camioneta</option>
                  <option value="Furgón">Furgón</option>
                  <option value="Semirremolque">Semirremolque</option>
                  <option value="Trailer">Trailer</option>
                </select>
              </div>
              {user?.rol === 'super_admin' && (
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">Empresa *</label>
                  <select
                    value={form.empresa_id}
                    onChange={(e) => setForm({ ...form, empresa_id: e.target.value })}
                    data-testid="vehicle-empresa-select"
                    required
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm bg-white"
                  >
                    <option value="">Selecciona empresa</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
              )}
              <div className="flex justify-end space-x-2 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200 text-sm font-medium">Cancelar</button>
                <button type="submit" data-testid="submit-vehicle-btn" className="px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 text-sm font-medium">Crear Vehículo</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
